import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "@/api/client";
import type { Cart } from "@/api/types";
import { sessionId } from "@/lib/session";

export const cartKeys = { cart: ["cart"] as const };

const EMPTY: Cart = { groups: [], saved: [], subtotal: 0, item_count: 0, has_issues: false, coupon: null };

export function useCart(enabled = true) {
  return useQuery({
    queryKey: cartKeys.cart,
    queryFn: () => unwrap(api.GET("/api/v1/cart/")),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

/** Item count for badges (0 while loading). */
export function useCartCount(): number {
  return useCart().data?.item_count ?? 0;
}

function useCartMutation<V>(fn: (v: V) => Promise<Cart>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (cart) => {
      qc.setQueryData(cartKeys.cart, cart);
      void qc.invalidateQueries({ queryKey: ["checkout", "quote"] });
    },
  });
}

export const useAddToCart = () =>
  useCartMutation(({ product, variant, quantity = 1 }: { product: string; variant?: string | null; quantity?: number }) =>
    unwrap(api.POST("/api/v1/cart/items/", { body: { product, variant: variant ?? null, quantity } })),
  );

export const useUpdateCartItem = () =>
  useCartMutation(({ id, quantity, saved_for_later }: { id: string; quantity?: number; saved_for_later?: boolean }) =>
    unwrap(api.PATCH("/api/v1/cart/items/{item_id}/", { params: { path: { item_id: id } }, body: { quantity, saved_for_later } })),
  );

export const useRemoveCartItem = () =>
  useCartMutation((id: string) => unwrap(api.DELETE("/api/v1/cart/items/{item_id}/", { params: { path: { item_id: id } } })));

export const useAcknowledgePrices = () => useCartMutation(() => unwrap(api.POST("/api/v1/cart/acknowledge-prices/")));

/** After sign-in, move the guest cart (same browser session id) into the account. */
export async function mergeGuestCart(): Promise<Cart> {
  return unwrap(api.POST("/api/v1/cart/merge/", { body: { session_id: sessionId() } }));
}

export { EMPTY as EMPTY_CART };

export const useApplyCoupon = () => useCartMutation((code: string) => unwrap(api.POST("/api/v1/cart/apply-coupon/", { body: { code } })));

export const useRemoveCoupon = () => useCartMutation(() => unwrap(api.DELETE("/api/v1/cart/apply-coupon/")));
